import {isFeatureEnabled} from '@github-ui/feature-flags'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'

import type {AgentAuthorizationState} from './types'

/**
 * Hardcoded set of agent slugs the mock treats as unauthorized. Empty by default;
 * real source of truth is `needs_authorization` from the API (see mockAgentAuthState).
 */
const MOCK_UNAUTHORIZED_SLUGS = new Set<string>([
  // 'anthropic-code-agent',
  // 'openai-code-agent',
])

let testOverrideSlugs: Set<string> | null = null

/**
 * Trailing segment of the 5-part Rails mention token
 * (`<token>:<vendor>:<slug>:<displayName>:<authorizationStatus>`).
 */
export type TokenAuthorizationStatus = 'authorized' | 'needs_authorization'

/**
 * Decodes the token's `authorizationStatus` segment into a boolean.
 * Returns `undefined` when the segment is absent (older 4-part token format).
 */
export function needsAuthorizationFromTokenStatus(status: TokenAuthorizationStatus | undefined): boolean | undefined {
  if (status === undefined) return undefined
  return status === 'needs_authorization'
}

/**
 * Mocked authorization state for an agent — fallback when the API field isn't available.
 * Returns `requiresAuthentication: false` unless `copilot_swe_agent_authorization_status_ui` is on.
 */
export function mockAgentAuthState(slug: string, displayName: string, avatarUrl?: string): AgentAuthorizationState {
  const flagEnabled = isFeatureEnabled('copilot_swe_agent_authorization_status_ui')
  const slugs = testOverrideSlugs ?? MOCK_UNAUTHORIZED_SLUGS
  const requiresAuthentication = flagEnabled && slugs.has(slug)

  return {
    requiresAuthentication,
    authorizationUrl: requiresAuthentication ? `/agents/${slug}/authorization/start` : undefined,
    agent: {slug, displayName, avatarUrl},
  }
}

/**
 * Requests the OAuth authorize URL for an agent. When `nwo` + `integrationId`
 * are provided, POSTs to the real authorize endpoint; otherwise returns a mock URL.
 */
export async function requestAuthorizationUrl(
  agent: {slug: string; integrationId?: number},
  options: {nwo?: string} = {},
): Promise<string> {
  const {nwo} = options
  if (nwo && typeof agent.integrationId === 'number') {
    const response = await verifiedFetchJSON(`/copilot/agent-sessions/${nwo}/agents/${agent.integrationId}/authorize`, {
      method: 'POST',
    })
    if (!response.ok) {
      throw new Error(`authorize endpoint returned ${response.status}`)
    }
    const json = (await response.json()) as {authorize_url?: string}
    if (!json.authorize_url) {
      throw new Error('authorize endpoint returned no authorize_url')
    }
    return json.authorize_url
  }

  // Mock fallback for surfaces that don't yet plumb `nwo`/`integrationId`
  // through (e.g. the composer banner, where the Catalyst event payload is
  // slug-only).
  await new Promise(resolve => setTimeout(resolve, 0))
  return `/agents/${agent.slug}/authorization/start`
}

/**
 * Test-only helper to override the unauthorized-slug set. Pass `null` to reset.
 */
export function __setMockUnauthorizedSlugsForTest(slugs: string[] | null): void {
  testOverrideSlugs = slugs ? new Set(slugs) : null
}
